import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Clock, ChevronRight, Calendar, Radio, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { ClaimStatusBadge, EvidenceDrawer } from "@/components/shared";
import { Reveal } from "@/components/collection";

/* ============================================================================
   BB-067A: Elvis Aloha 1973 — Event & Timeline Pages
   Theatrical chronology presentation
   ============================================================================ */

export const EventHubPage = () => (
  <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }} data-testid="event-hub-page">
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <Reveal>
        <p className="font-ui text-xs uppercase tracking-[0.25em] text-[hsl(43,74%,52%)] mb-4">
          January 14, 1973
        </p>
        <h1 className="font-display text-4xl md:text-5xl text-white mb-6">The Event</h1>
        <p className="font-editorial text-lg text-white/60 leading-relaxed max-w-2xl mb-12">
          Documentation of Elvis Presley&apos;s Aloha from Hawaii satellite concert, broadcast on January 14, 1973.
          This section provides an authoritative timeline with claim classifications and primary source citations.
        </p>
      </Reveal>
      
      <div className="grid gap-6 md:grid-cols-2">
        <Reveal delay={100}>
          <Link 
            to="/event/timeline" 
            className="group block p-8 bg-[hsl(222,47%,11%,0.5)] border border-white/10 hover:border-[hsl(43,74%,52%,0.4)] transition-colors"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center border border-[hsl(43,74%,52%)]">
                <Clock className="w-6 h-6 text-[hsl(43,74%,52%)]" />
              </div>
              <div>
                <h3 className="font-display text-xl text-white group-hover:text-[hsl(43,74%,52%)] transition-colors">
                  Event Timeline
                </h3>
                <p className="font-ui text-sm text-white/50">Chronological documentation</p>
              </div>
            </div>
            <p className="font-editorial text-white/60 leading-relaxed">
              From announcement to broadcast — a documented chronology of every major event 
              leading to and following the historic concert.
            </p>
            <span className="inline-flex items-center gap-2 mt-4 font-ui text-sm text-[hsl(43,74%,52%)]">
              View Timeline <ChevronRight className="w-4 h-4" />
            </span>
          </Link>
        </Reveal>
        
        <Reveal delay={150}>
          <Link 
            to="/studies" 
            className="group block p-8 bg-[hsl(222,47%,11%,0.5)] border border-white/10 hover:border-[hsl(43,74%,52%,0.4)] transition-colors"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center border border-[hsl(43,74%,52%)]">
                <Radio className="w-6 h-6 text-[hsl(43,74%,52%)]" />
              </div>
              <div>
                <h3 className="font-display text-xl text-white group-hover:text-[hsl(43,74%,52%)] transition-colors">
                  Broadcast Research
                </h3>
                <p className="font-ui text-sm text-white/50">Technical & geographic analysis</p>
              </div>
            </div>
            <p className="font-editorial text-white/60 leading-relaxed">
              Detailed examination of the satellite broadcast, transmission territories, 
              and the distinction between live and delayed transmission.
            </p>
            <span className="inline-flex items-center gap-2 mt-4 font-ui text-sm text-[hsl(43,74%,52%)]">
              View Studies <ChevronRight className="w-4 h-4" />
            </span>
          </Link>
        </Reveal>
      </div>
      
      {/* Key Event Facts */}
      <Reveal delay={200}>
        <div className="mt-16 pt-16 border-t border-white/10">
          <h2 className="font-display text-2xl text-white mb-8">Key Event Details</h2>
          <div className="grid gap-8 md:grid-cols-3">
            <div className="flex items-start gap-4">
              <Calendar className="w-6 h-6 text-[hsl(43,74%,52%)] flex-shrink-0 mt-1" />
              <div>
                <p className="font-ui text-xs uppercase tracking-[0.15em] text-[hsl(43,74%,52%)] mb-1">Concert Date</p>
                <p className="font-display text-lg text-white">January 14, 1973</p>
                <p className="font-ui text-sm text-white/50 mt-1">12:30 AM HST (Broadcast start)</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin className="w-6 h-6 text-[hsl(43,74%,52%)] flex-shrink-0 mt-1" />
              <div>
                <p className="font-ui text-xs uppercase tracking-[0.15em] text-[hsl(43,74%,52%)] mb-1">Venue</p>
                <p className="font-display text-lg text-white">H.I.C. Arena</p>
                <p className="font-ui text-sm text-white/50 mt-1">Honolulu, Hawaii</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Radio className="w-6 h-6 text-[hsl(43,74%,52%)] flex-shrink-0 mt-1" />
              <div>
                <p className="font-ui text-xs uppercase tracking-[0.15em] text-[hsl(43,74%,52%)] mb-1">Broadcast</p>
                <p className="font-display text-lg text-white">Satellite via Intelsat IV</p>
                <p className="font-ui text-sm text-white/50 mt-1">Live to Asia/Oceania; delayed elsewhere</p>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </div>
);

export const TimelinePage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    api.get("/timeline").then(res => {
      setEntries(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  
  if (loading) {
    return (
      <div className="flex justify-center py-20" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/20 border-t-[hsl(43,74%,52%)]"></div>
      </div>
    );
  }
  
  // Group entries by year/month for better hierarchy
  const groupedEntries = entries.reduce((acc, entry) => {
    const year = entry.date_start?.split('-')[0] || 'Unknown';
    if (!acc[year]) acc[year] = [];
    acc[year].push(entry);
    return acc;
  }, {});
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }} data-testid="timeline-page">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(222,47%,11%)] to-[hsl(0,0%,4%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,hsl(43,74%,52%,0.06)_0%,transparent_50%)]" />
        
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <Link to="/event" className="inline-flex items-center gap-2 font-ui text-sm text-[hsl(43,74%,52%)] hover:text-white transition-colors mb-6">
              ← Back to Event Hub
            </Link>
            <p className="font-ui text-xs uppercase tracking-[0.25em] text-[hsl(43,74%,52%)] mb-4">
              Documented Chronology
            </p>
            <h1 className="font-display text-4xl md:text-5xl text-white mb-6">Event Timeline</h1>
            <p className="font-editorial text-lg text-white/60 max-w-2xl leading-relaxed">
              From the September 1972 announcement through the January 1973 broadcast — 
              every documented event with evidence classification and primary source citations.
            </p>
          </Reveal>
        </div>
      </section>
      
      {/* Timeline */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="relative">
            {/* Vertical timeline line */}
            <div className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[hsl(43,74%,52%)] via-[hsl(43,74%,52%,0.5)] to-transparent"></div>
            
            <div className="space-y-12">
              {Object.entries(groupedEntries).map(([year, yearEntries]) => (
                <div key={year}>
                  {/* Year marker */}
                  <div className="relative flex items-center mb-8">
                    <div className="w-12 md:w-16 h-12 md:h-16 flex items-center justify-center bg-[hsl(43,74%,52%)] text-black font-display text-xl md:text-2xl font-bold z-10">
                      {year}
                    </div>
                    <div className="ml-6 flex-1 border-t border-[hsl(43,74%,52%,0.3)]"></div>
                  </div>
                  
                  <div className="space-y-8 ml-16 md:ml-20">
                    {yearEntries.map((entry, idx) => (
                      <Reveal key={entry.id} delay={Math.min(idx * 50, 200)}>
                        <div className="relative" data-testid={`timeline-entry-${entry.id}`}>
                          {/* Diamond marker */}
                          <div className="absolute -left-[52px] md:-left-[60px] top-2 w-3 h-3 bg-[hsl(0,0%,4%)] border-2 border-[hsl(43,74%,52%)] transform rotate-45"></div>
                          
                          <article className="p-6 md:p-8 bg-[hsl(222,47%,11%,0.5)] border border-white/10 hover:border-[hsl(43,74%,52%,0.3)] transition-colors">
                            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                              <div>
                                <p className="font-ui text-sm text-[hsl(43,74%,52%)] mb-2">{entry.date_start}</p>
                                <h3 className="font-display text-xl md:text-2xl text-white">{entry.title}</h3>
                              </div>
                              {entry.status_rollup && <ClaimStatusBadge status={entry.status_rollup} />}
                            </div>
                            
                            <p className="font-editorial text-white/70 leading-relaxed">{entry.narrative}</p>
                            
                            {entry.claim_ids?.length > 0 && (
                              <div className="mt-5 pt-5 border-t border-white/10">
                                <EvidenceDrawer claimIds={entry.claim_ids} title={`Evidence: ${entry.title}`} />
                              </div>
                            )}
                            
                            {entry.tags?.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-4">
                                {entry.tags.map((tag, i) => (
                                  <Badge key={i} variant="secondary" className="font-ui text-xs bg-white/5 text-white/60 border-white/10">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </article>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              ))}
              
              {entries.length === 0 && (
                <div className="text-center py-16 text-white/50 font-editorial ml-16">
                  No timeline entries have been documented yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventHubPage;
